import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';
import { useLocalizedPath } from '../hooks/useLocalizedPath';
import { useI18nLocaleSync } from '../hooks/useI18nLocaleSync';
import { useAppLocale } from '../hooks/useAppLocale';
import { pathWithLocale } from '../lib/locale';

export function Guide() {
  const { t } = useTranslation();
  const locale = useAppLocale();
  const lp = useLocalizedPath();

  useI18nLocaleSync();

  usePageMeta({
    title: t('guidePage.metaTitle'),
    description: t('guidePage.metaDesc'),
    path: pathWithLocale('/guide', locale),
  });

  const pbTiers = t('guidePage.pbTiers', { returnObjects: true }) as string[];
  const pbJackpots = t('guidePage.pbJackpots', { returnObjects: true }) as string[];
  const mmTiers = t('guidePage.mmTiers', { returnObjects: true }) as string[];
  const mmJackpots = t('guidePage.mmJackpots', { returnObjects: true }) as string[];
  const krTiers = t('guidePage.krTiers', { returnObjects: true }) as string[];
  const responsibleTips = t('guidePage.responsibleTips', { returnObjects: true }) as string[];

  return (
    <main className="content">
      <nav className="breadcrumb" aria-label={t('layout.breadcrumbLabel')}>
        <Link to={lp('/')}>{t('breadcrumb.home')}</Link>
        <span className="sep">/</span>
        <span className="current">{t('guidePage.breadcrumb')}</span>
      </nav>

      <article className="about-section">
        <h1>{t('guidePage.title')}</h1>
        <p>
          <Trans i18nKey="guidePage.intro" components={{ home: <Link to={lp('/')} /> }} />
        </p>

        {/* POWERBALL */}
        <h2 style={{ fontSize: '1.2em', marginTop: 28, marginBottom: 8 }}>
          {t('guidePage.pbTitle')}
        </h2>
        <p>{t('guidePage.pbIntro')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 20, marginBottom: 6 }}>{t('guidePage.pbHowTitle')}</h3>
        <p>{t('guidePage.pbHowBody')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 16, marginBottom: 6 }}>
          {t('guidePage.pbOddsTitle')}
        </h3>
        <p>{t('guidePage.pbOddsIntro')}</p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {pbTiers.map((tier, i) => <li key={i}>{tier}</li>)}
        </ul>

        <h3 style={{ fontSize: '1.08em', marginTop: 16, marginBottom: 6 }}>
          {t('guidePage.pbJackpotsTitle')}
        </h3>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {pbJackpots.map((jackpot, i) => <li key={i}>{jackpot}</li>)}
        </ul>

        {/* MEGA MILLIONS */}
        <h2 style={{ fontSize: '1.2em', marginTop: 32, marginBottom: 8 }}>
          {t('guidePage.mmTitle')}
        </h2>
        <p>{t('guidePage.mmIntro')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 20, marginBottom: 6 }}>{t('guidePage.mmHowTitle')}</h3>
        <p>{t('guidePage.mmHowBody')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 16, marginBottom: 6 }}>
          {t('guidePage.mmOddsTitle')}
        </h3>
        <p>{t('guidePage.mmOddsIntro')}</p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {mmTiers.map((tier, i) => <li key={i}>{tier}</li>)}
        </ul>

        <h3 style={{ fontSize: '1.08em', marginTop: 16, marginBottom: 6 }}>
          {t('guidePage.mmJackpotsTitle')}
        </h3>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {mmJackpots.map((jackpot, i) => <li key={i}>{jackpot}</li>)}
        </ul>

        {/* KOREAN LOTTO */}
        <h2 style={{ fontSize: '1.2em', marginTop: 32, marginBottom: 8 }}>{t('guidePage.krTitle')}</h2>
        <p>{t('guidePage.krIntro')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 20, marginBottom: 6 }}>
          {t('guidePage.krHowTitle')}
        </h3>
        <p>{t('guidePage.krHowBody')}</p>

        <h3 style={{ fontSize: '1.08em', marginTop: 16, marginBottom: 6 }}>
          {t('guidePage.krOddsTitle')}
        </h3>
        <p>{t('guidePage.krOddsIntro')}</p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {krTiers.map((tier, i) => <li key={i}>{tier}</li>)}
        </ul>

        {/* ODDS EXPLANATION */}
        <h2 style={{ fontSize: '1.2em', marginTop: 32, marginBottom: 8 }}>
          {t('guidePage.oddsTitle')}
        </h2>
        <p>{t('guidePage.oddsIntro')}</p>
        <ul style={{ margin: '8px 0 0 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          <li>
            <strong>Powerball:</strong> C(69,5) × 26 = 11,238,513 × 26 ={' '}
            <strong>292,201,338</strong>
          </li>
          <li>
            <strong>Mega Millions:</strong> C(70,5) × 24 = 12,103,014 × 24 ={' '}
            <strong>290,472,336</strong>
          </li>
          <li>
            <strong>Korean Lotto 6/45:</strong> C(45,6) ={' '}
            <strong>8,145,060</strong>
          </li>
        </ul>
        <p>{t('guidePage.oddsTickets')}</p>
        <p>{t('guidePage.oddsHotCold')}</p>

        {/* HOW THE GENERATOR WORKS */}
        <h2 style={{ fontSize: '1.2em', marginTop: 32, marginBottom: 8 }}>
          {t('guidePage.howTitle')}
        </h2>
        <p>{t('guidePage.howPrng')}</p>
        <p>
          <Trans
            i18nKey="guidePage.howMatching"
            components={{ about: <Link to={lp('/about')} />, simulate: <Link to={lp('/simulate')} /> }}
          />
        </p>

        {/* RESPONSIBLE GAMING */}
        <h2 style={{ fontSize: '1.2em', marginTop: 32, marginBottom: 8 }}>{t('guidePage.responsibleTitle')}</h2>
        <p>{t('guidePage.responsibleEv')}</p>
        <ul style={{ margin: '8px 0 12px 20px', lineHeight: 1.85, color: 'var(--text-secondary)' }}>
          {responsibleTips.map((tip, i) => <li key={i}>{tip}</li>)}
        </ul>
        <p>
          <Trans
            i18nKey="guidePage.responsibleLinks"
            components={{
              about: <Link to={lp('/about')} />,
              faq: <Link to={lp('/faq')} />,
              terms: <Link to={lp('/terms')} />,
            }}
          />
        </p>

        <div className="disclaimer">
          {t('guidePage.disclaimer')}
        </div>
      </article>
    </main>
  );
}
